import type { DrawingViewGesture } from './Drawing';
type Point = { x: number; y: number };
/** Tracks the whole painting viewport, including the space around the canvas. */
export class WallNavigation {
  private pointers = new Map<number, Point>();
  private start: { origin: Point; distance: number } | null = null;
  get navigating() { return this.start !== null; }
  get ids() { return [...this.pointers.keys()]; }
  private centre(): Point {
    const points = [...this.pointers.values()];
    return points.length > 1 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : points[0];
  }
  private distance() {
    const points = [...this.pointers.values()];
    return points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0;
  }
  private begin(): DrawingViewGesture {
    const origin = this.centre();
    this.start = { origin, distance: this.distance() };
    return { phase: 'start', origin, point: origin, scale: 1 };
  }
  down(id: number, point: Point, panning = false): DrawingViewGesture | null {
    this.pointers.set(id, point);
    return this.pointers.size > 1 || panning || this.navigating ? this.begin() : null;
  }
  move(id: number, point: Point): DrawingViewGesture | null {
    if (!this.pointers.has(id)) return null;
    this.pointers.set(id, point);
    return this.start ? { phase: 'move', origin: this.start.origin, point: this.centre(), scale: this.start.distance > 0 ? this.distance() / this.start.distance : 1 } : null;
  }
  /** UI may block new input, but releases must always retire tracked fingers. */
  pointer(phase: 'down' | 'move' | 'up', id: number, point: Point, panning = false, blocked = false): DrawingViewGesture | null {
    if (phase === 'up') return this.up(id);
    if (blocked) return null;
    return phase === 'down' ? this.down(id, point, panning) : this.move(id, point);
  }
  up(id: number): DrawingViewGesture | null {
    if (!this.pointers.has(id)) return null;
    this.pointers.delete(id);
    if (!this.start) return null;
    if (this.pointers.size) return this.begin();
    const origin = this.start.origin;
    this.start = null;
    return { phase: 'end', origin, point: origin, scale: 1 };
  }
}


/** Retire scene ownership before capture takeover; pending captures need not emit lost-capture. */
export function takeViewportPointers(ids: readonly number[], cancelSceneGesture: () => void, beginDrawingNavigation: () => void, capturePointer: (id: number) => void): void {
  cancelSceneGesture();
  beginDrawingNavigation();
  for (const id of ids) capturePointer(id);
}

/** A finished viewport gesture/target leaves neither controller owning scene input. */
export function releaseViewportPointers(cancelSceneGesture: () => void, endDrawingNavigation: () => void): void {
  cancelSceneGesture();
  endDrawingNavigation();
}
